import { observer } from 'mobx-react-lite';
import { bulkAction_cancel } from './BulkActionProgressBar.locstring.json';
import loc from 'owa-localize';
/* tslint:disable:jsx-no-lambda WI:47775 */
import * as React from 'react';
import { BulkActionScenarioType } from 'owa-bulk-action-store';
import clearTableViewLastEmptiedTime from 'owa-mail-triage-action/lib/actions/clearTableViewLastEmptiedTime';
import getBulkActionStatusText from '../selectors/getBulkActionStatusText';
import reloadTable from 'owa-mail-triage-table-load-extra/lib/actions/reloadTable';
import { isReadingPanePositionOff } from 'owa-mail-layout';

import { ActionButton } from '@fluentui/react/lib/Button';
import { ControlIcons } from 'owa-control-icons';
import { Icon } from '@fluentui/react/lib/Icon';
import { IconButton } from '@fluentui/react/lib/Button';
import { listViewStore } from 'owa-mail-list-store';
import { MailIcons } from 'owa-mail-icons';
import { ProgressIndicator } from '@fluentui/react/lib/ProgressIndicator';
import {
    BulkActionInformation,
    BulkActionStateEnum,
    store,
    lazyOnBulkActionCancel,
    lazyOnBulkActionDismiss,
} from 'owa-bulk-action-store';
import isSenderImageEnabled from 'owa-sender-image-option/lib/utils/isSenderImageEnabled';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';

const styles = require('./BulkActionProgressBar.scss');
const classNames = require('classnames');

export default observer(function BulkActionProgressBar(props: {}) {
    React.useEffect(() => {
        return () => {
            const bulkActionState = store.bulkActionInformationMap.get(folderId.current);
            // Clear bulk state only if the state has not already been cleared (if user pressed dismiss)
            if (
                bulkActionState &&
                (bulkActionState.state === BulkActionStateEnum.Complete ||
                    bulkActionState.state === BulkActionStateEnum.Cancelled)
            ) {
                lazyOnBulkActionDismiss.import().then(onBulkActionDismiss => {
                    onBulkActionDismiss(folderId.current);
                });
            }
        };
    }, []);
    const folderId = React.useRef<string>();
    const onClickDismiss = () => {
        lazyOnBulkActionDismiss.import().then(onBulkActionDismiss => {
            onBulkActionDismiss(folderId.current);
        });
    };
    const onClickStop = (scenario: BulkActionScenarioType) => {
        const tableView = listViewStore.tableViews.get(listViewStore.selectedTableViewId);
        clearTableViewLastEmptiedTime(tableView);
        lazyOnBulkActionCancel.import().then(onBulkActionCancel => {
            onBulkActionCancel(folderId.current).then(isSuccessful => {
                if (isSuccessful && scenario == 'EmptyFolder') {
                    reloadTable(tableView);
                }
            });
        });
    };
    const tableView = listViewStore.tableViews.get(listViewStore.selectedTableViewId);
    folderId.current = tableView.tableQuery.folderId;
    const bulkActionInformation: BulkActionInformation = store.bulkActionInformationMap.get(
        folderId.current
    );
    const customData: any =
        bulkActionInformation.customData && JSON.parse(bulkActionInformation.customData);
    const scenario: BulkActionScenarioType = customData
        ? customData.Scenario
        : bulkActionInformation.class;
    const statusText = getBulkActionStatusText(bulkActionInformation, scenario, customData);
    const isBulkActionRunning = bulkActionInformation.state === BulkActionStateEnum.Running;
    // Some scenarios don't have granular enough updates for a progress bar
    const shouldHideProgress = scenario == 'RunRuleNow' || scenario == 'Sweep';
    return (
        <div className={styles.bulkActionNotificationBarContainer}>
            <div className={styles.progressDetailsContainer}>
                {getBulkActionStatusIcon(bulkActionInformation, scenario)}
                <span className={styles.contentText} title={statusText}>
                    {statusText}
                </span>
                {isBulkActionRunning ? (
                    <ActionButton
                        className={styles.cancelButton}
                        text={loc(bulkAction_cancel)}
                        onClick={evt => onClickStop(scenario)}
                    />
                ) : (
                    <IconButton
                        className={styles.menuButton}
                        onClick={evt => onClickDismiss()}
                        iconProps={{
                            iconName: ControlIcons.Cancel,
                        }}
                    />
                )}
            </div>
            {isBulkActionRunning && !shouldHideProgress && (
                <ProgressIndicator
                    className={styles.progressIndicator}
                    percentComplete={bulkActionInformation.progress.percentComplete}
                />
            )}
        </div>
    );
});

function getBulkActionSpecificIcon(scenario: BulkActionScenarioType) {
    switch (scenario) {
        case 'EmptyFolder':
            return ControlIcons.Delete;
        case 'Sweep':
            return MailIcons.Broom;
        case 'RunRuleNow':
            return ControlIcons.Play;
        default:
            return ControlIcons.Help; // If we don't know bulk action class, just make it a question mark by default
    }
}

function getBulkActionStatusIcon(
    bulkActionInfo: BulkActionInformation,
    scenario: BulkActionScenarioType
): JSX.Element {
    const tableView = listViewStore.tableViews.get(listViewStore.selectedTableViewId);
    const isItemView = tableView?.tableQuery?.listViewType === ReactListViewType.Message;
    let statusIcon = null;
    let iconColorClassName = classNames(
        isSenderImageEnabled() && styles.senderImageOn,
        isReadingPanePositionOff() && !isItemView && styles.SLVItemView,
        styles.statusIcon
    );
    switch (bulkActionInfo.state) {
        case BulkActionStateEnum.Complete:
            statusIcon = ControlIcons.CheckMark;
            iconColorClassName = styles.success;
            break;
        case BulkActionStateEnum.Failed:
            statusIcon = ControlIcons.Error;
            iconColorClassName = styles.failure;
            break;
        case BulkActionStateEnum.Cancelled:
            statusIcon = ControlIcons.Error;
            break;
        case BulkActionStateEnum.Cancelling:
        default:
            statusIcon = getBulkActionSpecificIcon(scenario);
            break;
    }
    return <Icon className={iconColorClassName} iconName={statusIcon} />;
}
